import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AssetsService, SearchableMenuItem } from '../../../core/services/assets.service';
import {
  BusinessDocument,
  BusinessDocumentListPage,
} from '../../../core/services/business-document.service';
import { BusinessPartnerRecord, BusinessPartnerService } from '../../../core/services/business-partner.service';
import { EmploymentServices } from '../../../core/services/employment.services';

export interface HeaderSearchResult extends SearchableMenuItem {
  resultType: 'Page' | 'Document' | 'Employee' | 'Contact' | 'Customer';
}

@Injectable({
  providedIn: 'root',
})
export class HeaderService {
  private searchablePages: SearchableMenuItem[] = [];
  private enabledAppIcons = new Map<string, string>();
  private initialization?: Promise<void>;

  constructor(
    private readonly assetsService: AssetsService,
    private readonly businessDocuments: BusinessDocument,
    private readonly employments: EmploymentServices,
    private readonly partners: BusinessPartnerService,
  ) {
    this.assetsService.appVisibilityChanged.subscribe(() => {
      this.initialization = undefined;
      this.searchablePages = [];
      this.enabledAppIcons.clear();
    });
  }

  initialize(): Promise<void> {
    this.initialization ??= this.loadSearchContext();
    return this.initialization;
  }

  async search(query: string): Promise<HeaderSearchResult[]> {
    await this.initialize();
    const normalizedQuery = query.trim().toLocaleLowerCase();
    const terms = normalizedQuery.split(/\s+/).filter(Boolean);
    if (!terms.length) return [];

    const pageResults = this.searchPages(terms, normalizedQuery);
    const recordResults = await this.searchRecords(query.trim());
    return [...pageResults, ...recordResults].slice(0, 30);
  }

  private async loadSearchContext(): Promise<void> {
    try {
      const [pages, apps] = await Promise.all([
        this.assetsService.getSearchableMenuItems(),
        this.assetsService.getEnabledAppsUnarranged(),
      ]);
      this.searchablePages = pages;
      this.enabledAppIcons = new Map(apps.map((app) => [this.appRoot(app.route), app.icon]));
    } catch (error) {
      this.initialization = undefined;
      throw error;
    }
  }

  private searchPages(terms: string[], normalizedQuery: string): HeaderSearchResult[] {
    return this.searchablePages
      .filter((page) => terms.every((term) => page.searchText.includes(term)))
      .map((page) => ({ ...page, resultType: 'Page' as const }))
      .sort((left, right) => {
        const leftName = left.name.toLocaleLowerCase();
        const rightName = right.name.toLocaleLowerCase();
        const leftRank = leftName === normalizedQuery ? 0 : leftName.startsWith(normalizedQuery) ? 1 : 2;
        const rightRank = rightName === normalizedQuery ? 0 : rightName.startsWith(normalizedQuery) ? 1 : 2;
        return leftRank - rightRank || left.breadcrumb.localeCompare(right.breadcrumb);
      })
      .slice(0, 12);
  }

  private async searchRecords(query: string): Promise<HeaderSearchResult[]> {
    const searches: Array<Promise<HeaderSearchResult[]>> = [];
    if (this.enabledAppIcons.has('/documents')) searches.push(this.searchBusinessDocuments(query));
    if (this.enabledAppIcons.has('/hr')) searches.push(this.searchEmployments(query));
    if (this.enabledAppIcons.has('/configuration') || this.enabledAppIcons.has('/sales')) {
      searches.push(this.searchBusinessPartners(query));
    }

    const settled = await Promise.allSettled(searches);
    return settled.flatMap((result) => result.status === 'fulfilled' ? result.value : []);
  }

  private async searchBusinessDocuments(query: string): Promise<HeaderSearchResult[]> {
    const response = await firstValueFrom(this.businessDocuments.listDocuments({
      query,
      searchColumns: ['document_number', 'display_name', 'reference'],
      filters: [],
      page: 1,
      perPage: 6,
    })) as BusinessDocumentListPage;
    return (response.data ?? []).map((document) => this.recordResult({
      name: document.document_number || document.display_name,
      description: [document.display_name, document.pipeline_step_name || document.stage]
        .filter(Boolean).join(' · '),
      breadcrumb: `Documents / ${document.document_type_key}`,
      link: `/documents/${document.id}/view`,
      resultType: 'Document',
      iconRoot: '/documents',
      searchText: [document.document_number, document.display_name, document.reference, document.document_type_key]
        .join(' ').toLocaleLowerCase(),
    }));
  }

  private async searchEmployments(query: string): Promise<HeaderSearchResult[]> {
    const response: any = await this.employments.getAll({
      query,
      searchColumns: ['user_first_name', 'user_last_name', 'user_email', 'employment_position_name'],
      filters: [],
      page: 1,
      perPage: 6,
    });
    return (response?.data ?? []).map((employment: any) => {
      const name = [employment.user_first_name, employment.user_last_name].filter(Boolean).join(' ').trim()
        || employment.user_email
        || 'Employee';
      return this.recordResult({
        name,
        description: [employment.employment_position_name, employment.user_email].filter(Boolean).join(' · '),
        breadcrumb: 'Human Resource Management / Employees',
        link: `/hr/employment/${employment.id}`,
        resultType: 'Employee',
        iconRoot: '/hr',
        searchText: [name, employment.user_email, employment.employment_position_name, employment.department_name]
          .join(' ').toLocaleLowerCase(),
      });
    });
  }

  private async searchBusinessPartners(query: string): Promise<HeaderSearchResult[]> {
    const partners = await this.partners.listPartners({ query });
    return partners.slice(0, 8).map((partner: BusinessPartnerRecord) => {
      const customer = partner.is_customer && this.enabledAppIcons.has('/sales');
      return this.recordResult({
        name: partner.name,
        description: [partner.company, partner.email, partner.phone].filter(Boolean).join(' · '),
        breadcrumb: customer ? 'Sales Management / Customers' : 'Configuration / Contacts & Partners',
        link: customer
          ? `/sales/customers/${partner.id}`
          : `/configuration/contacts-partners/customers/${partner.id}`,
        resultType: customer ? 'Customer' : 'Contact',
        iconRoot: customer ? '/sales' : '/configuration',
        searchText: [partner.name, partner.company, partner.email, partner.phone, partner.mobile]
          .join(' ').toLocaleLowerCase(),
      });
    });
  }

  private recordResult(input: {
    name: string;
    description: string;
    breadcrumb: string;
    link: string;
    resultType: HeaderSearchResult['resultType'];
    iconRoot: string;
    searchText: string;
  }): HeaderSearchResult {
    return {
      name: input.name,
      link: input.link,
      icon: undefined,
      description: input.description,
      keywords: [],
      children: [],
      appName: input.breadcrumb.split(' / ')[0],
      appIcon: this.enabledAppIcons.get(input.iconRoot) ?? this.enabledAppIcons.get('/configuration') ?? '',
      breadcrumb: input.breadcrumb,
      searchText: input.searchText,
      resultType: input.resultType,
    };
  }

  private appRoot(route: string): string {
    return `/${String(route || '').split(/[?#]/)[0].split('/').filter(Boolean)[0] ?? ''}`;
  }
}
